import { lazy, Suspense, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Badge,
  Button,
  EmptyState,
  InlineError,
  Input,
  Panel,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import {
  durationLabel,
  longDate,
  splitByDate,
  statusLabel,
  statusTone,
  todayIso,
  withdrawAction,
} from './leaveLens';
import { ApprovalQueue } from './ApprovalQueue';
import { AllRequests } from './AllRequests';
import { LEAVE_TAB_LABELS, initialTab, settleTab, tabsFor } from './leaveTabs';
import type { LeaveTab } from './leaveTabs';
/**
 * FullCalendar is the biggest thing on this page by a distance, and nothing
 * else in the Hub uses it. Loaded on demand so the other eleven pages do not
 * carry it in the main bundle — the calendar arrives a moment after the rest
 * of the screen, which is the right trade for a board people scroll rather
 * than glance at.
 */
const LeaveCalendar = lazy(() =>
  import('./LeaveCalendar').then((module) => ({ default: module.LeaveCalendar })),
);
import {
  leaveErrorMessage,
  useFileLeave,
  useIsLeaveApprover,
  useLeaveReason,
  useMyLeave,
  useWithdrawLeave,
  type DurationType,
  type LeaveRequest,
  type LeaveType,
} from './queries';
import './leave.css';

/**
 * Leaves — filing a day off, and seeing your own.
 *
 * Drop 2 of 5. Deliberately only your own: the approver queue is drop 3 and
 * the team calendar is drop 4. Shipping this alone is still useful, because
 * the nine can stop filing in the Sheet today.
 *
 * A REASON IS NEVER SHOWN IN THE LIST. It is fetched one request at a time,
 * only when somebody opens that request, and only for the person who filed it
 * or an approver. See queries.ts for why.
 */
export function LeavePage() {
  const mine = useMyLeave();
  const isApprover = useIsLeaveApprover();
  const today = todayIso();

  // The URL decides the first tab, so a link into All Requests lands there.
  // Read once rather than kept in sync: these are in-page navigation, and a
  // tab click is not something anybody expects to add to their history.
  const [searchParams] = useSearchParams();
  const approver = isApprover.data === true;
  const [tab, setTab] = useState<LeaveTab>(() => initialTab(searchParams.get('tab'), approver));

  // useIsLeaveApprover resolves AFTER the first render, so the page draws once
  // before it knows. A member who arrived on ?tab=all would otherwise sit on a
  // screen whose query the database is about to refuse, and read an error
  // instead of a page. settleTab puts them back on Calendar the moment the
  // answer arrives, and does nothing at all in the normal case.
  const shown = settleTab(tab, approver);
  const visible = tabsFor(approver);

  return (
    <>
      <PageHeader
        title="Leaves"
        description="File a day off and track where it got to. Only you and an approver can see why you asked."
        topbar
      />
      <Panel>
        <nav className="tracker-tab-nav">
          {visible.map((key) => (
            <button
              key={key}
              type="button"
              className={`tracker-tab-button${shown === key ? ' tracker-tab-button--active' : ''}`}
              onClick={() => {
                setTab(key);
              }}
            >
              {LEAVE_TAB_LABELS[key]}
            </button>
          ))}
        </nav>

        {shown === 'calendar' && (
          <Suspense fallback={<Skeleton />}>
            <LeaveCalendar />
          </Suspense>
        )}

        {/* Request Leave carries the form AND this person's own requests.
            In the Apps Script a member checks their own history through All
            Requests; making that tab approver-only took the route away, so it
            comes back here. Nothing new is built — this is the list drops 1
            and 2 already shipped, given a home. */}
        {shown === 'request' && (
          <div className="leave-layout">
            <FileLeaveForm />
            <section className="leave-list-column">
              {mine.isLoading ? (
                <Skeleton />
              ) : mine.isError ? (
                <InlineError>{leaveErrorMessage(String(mine.error))}</InlineError>
              ) : (
                <MyLeaveList requests={mine.data ?? []} today={today} />
              )}
            </section>
          </div>
        )}

        {shown === 'all' && <AllRequests today={today} />}

        {shown === 'approvals' && <ApprovalQueue />}
      </Panel>
    </>
  );
}

function FileLeaveForm() {
  const file = useFileLeave();
  const [leaveDate, setLeaveDate] = useState('');
  const [leaveType, setLeaveType] = useState<LeaveType>('PTO');
  const [durationType, setDurationType] = useState<DurationType>('Full Day');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('13:00');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [filed, setFiled] = useState(false);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setFiled(false);
    file.mutate(
      {
        leaveDate,
        leaveType,
        durationType,
        ...(durationType === 'Half Day' ? { startTime, endTime } : {}),
        reason,
      },
      {
        onSuccess: () => {
          setFiled(true);
          setLeaveDate('');
          setReason('');
        },
        onError: (err: Error) => {
          setError(leaveErrorMessage(err.message));
        },
      },
    );
  };

  return (
    <Panel title="Ask for a day off">
      <form className="leave-form" onSubmit={submit}>
        {error === '' ? null : <InlineError>{error}</InlineError>}
        {filed ? <p className="leave-filed">Filed. Kyu or Alex will decide it.</p> : null}

        <Input
          label="Date"
          type="date"
          required
          value={leaveDate}
          onChange={(event) => {
            setLeaveDate(event.target.value);
          }}
        />

        <Select
          label="Type"
          value={leaveType}
          options={[
            { value: 'PTO', label: 'PTO' },
            { value: 'Unpaid Leave', label: 'Unpaid Leave' },
          ]}
          onChange={(event) => {
            setLeaveType(event.target.value as LeaveType);
          }}
        />

        <Select
          label="How long"
          value={durationType}
          options={[
            { value: 'Full Day', label: 'Full Day' },
            { value: 'Half Day', label: 'Half Day' },
          ]}
          onChange={(event) => {
            setDurationType(event.target.value as DurationType);
          }}
        />

        {durationType === 'Half Day' ? (
          <div className="leave-hours">
            <Input
              label="From"
              type="time"
              required
              value={startTime}
              onChange={(event) => {
                setStartTime(event.target.value);
              }}
            />
            <Input
              label="To"
              type="time"
              required
              value={endTime}
              onChange={(event) => {
                setEndTime(event.target.value);
              }}
            />
          </div>
        ) : null}

        <Textarea
          label="Reason"
          rows={3}
          maxLength={500}
          value={reason}
          hint="Only you and an approver can read this. It is never shown in a list, a calendar or an email."
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />

        <Button type="submit" disabled={file.isPending || leaveDate === ''}>
          {file.isPending ? 'Filing...' : 'File request'}
        </Button>
      </form>
    </Panel>
  );
}

function MyLeaveList({ requests, today }: { requests: LeaveRequest[]; today: string }) {
  if (requests.length === 0) {
    return (
      <EmptyState
        title="No leave yet"
        description="Anything you file shows up here with where it got to."
      />
    );
  }
  const { upcoming, past } = splitByDate(requests, today);
  return (
    <>
      {upcoming.length > 0 ? (
        <Panel title="Coming up">
          <ul className="leave-rows">
            {upcoming.map((request) => (
              <LeaveRow key={request.id} request={request} />
            ))}
          </ul>
        </Panel>
      ) : null}
      {past.length > 0 ? (
        <Panel title="Earlier">
          <ul className="leave-rows">
            {past.map((request) => (
              <LeaveRow key={request.id} request={request} />
            ))}
          </ul>
        </Panel>
      ) : null}
    </>
  );
}

function LeaveRow({ request }: { request: LeaveRequest }) {
  const [open, setOpen] = useState(false);
  const withdraw = useWithdrawLeave();
  const [error, setError] = useState('');
  const action = withdrawAction(request.status);
  // Only fetched once the row is opened. A list that pre-loaded every reason
  // would put them all in the network tab to render a screen showing none.
  const priv = useLeaveReason(open ? request.id : null);

  return (
    <li className="leave-row">
      <div className="leave-row-main">
        <div className="leave-row-when">
          <span className="leave-row-date">{longDate(request.leave_date)}</span>
          <span className="leave-row-meta">
            {request.leave_type} &middot; {durationLabel(request)}
          </span>
        </div>
        <Badge tone={statusTone(request.status)}>{statusLabel(request.status)}</Badge>
      </div>

      <div className="leave-row-actions">
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(!open);
          }}
          aria-expanded={open}
        >
          {open ? 'Hide detail' : 'Detail'}
        </Button>
        {action.can ? (
          <Button
            variant="ghost"
            disabled={withdraw.isPending}
            onClick={() => {
              setError('');
              withdraw.mutate(request.id, {
                onError: (err: Error) => {
                  setError(leaveErrorMessage(err.message));
                },
              });
            }}
          >
            {action.label}
          </Button>
        ) : null}
      </div>

      {error === '' ? null : <InlineError>{error}</InlineError>}

      {open ? (
        <div className="leave-row-detail">
          {priv.isLoading ? (
            <Skeleton />
          ) : priv.isError ? (
            <InlineError>{leaveErrorMessage(String(priv.error))}</InlineError>
          ) : (
            <>
              <p className="leave-detail-label">Reason</p>
              <p className="leave-detail-value">
                {priv.data?.reason === '' || priv.data === undefined
                  ? 'None given.'
                  : priv.data.reason}
              </p>
              {priv.data?.adminNote !== undefined && priv.data.adminNote !== '' ? (
                <>
                  <p className="leave-detail-label">Note from the approver</p>
                  <p className="leave-detail-value">{priv.data.adminNote}</p>
                </>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}
